import * as Dialog from '@radix-ui/react-dialog';
import { useState, type ChangeEvent } from 'react';
import { apiClient } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { SharePayload } from '../types';
import { toast } from 'sonner';

interface ShareDialogProps {
  fileId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ShareDialog({ fileId, open, onOpenChange }: ShareDialogProps) {
  const { token } = useAuth();
  const [password, setPassword] = useState('');
  const [expiresIn, setExpiresIn] = useState('7');
  const [isDirect, setIsDirect] = useState(true);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleShare = async () => {
    if (!fileId) return;
    setLoading(true);
    try {
      const payload: SharePayload = {
        fileId,
        isDirectLink: isDirect,
      };

      if (password.trim()) {
        payload.password = password;
      }

      if (expiresIn !== 'never') {
        const expiresAt = new Date(Date.now() + Number(expiresIn) * 24 * 60 * 60 * 1000).toISOString();
        payload.expiresAt = expiresAt;
      }

      const share = await apiClient.createShare(token, payload);
      setShareUrl(`${window.location.origin}/s/${share.code}`);
      toast.success('分享链接已生成');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '生成失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur" />
        <Dialog.Content className="supports-backdrop:glass-panel fixed left-1/2 top-1/2 w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-3xl bg-surface/90 p-6 text-white shadow-glass">
          <Dialog.Title className="text-xl font-semibold">分享文件</Dialog.Title>
          <div className="mt-4 space-y-4 text-sm text-white/80">
            <label className="flex flex-col gap-1">
              <span>访问密码（可选）</span>
              <input
                value={password}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setPassword(event.target.value)}
                placeholder="不设置则公开访问"
                className="rounded-2xl border border-white/10 bg-transparent px-4 py-2 outline-none focus:border-brand"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span>链接有效期</span>
              <select
                value={expiresIn}
                onChange={(event: ChangeEvent<HTMLSelectElement>) => setExpiresIn(event.target.value)}
                className="rounded-2xl border border-white/10 bg-transparent px-4 py-2"
              >
                <option value="1">1 天</option>
                <option value="7">7 天</option>
                <option value="30">30 天</option>
                <option value="never">永久</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-white/70">
              <input type="checkbox" checked={isDirect} onChange={(event: ChangeEvent<HTMLInputElement>) => setIsDirect(event.target.checked)} />
              生成可直接访问的直链
            </label>
          </div>

          {shareUrl && (
            <div className="mt-4 rounded-2xl border border-white/10 bg-black/40 p-4 text-xs">
              <p className="text-white/60">分享链接</p>
              <p className="truncate text-white">{shareUrl}</p>
              <button
                className="mt-3 rounded-full border border-white/20 px-4 py-1 text-white/80"
                onClick={() => navigator.clipboard.writeText(shareUrl)}
              >
                复制
              </button>
            </div>
          )}

          <div className="mt-6 flex justify-end gap-3">
            <Dialog.Close className="rounded-full border border-white/10 px-4 py-2 text-white/70">取消</Dialog.Close>
            <button
              className="rounded-full bg-gradient-to-r from-brand to-accent px-6 py-2 font-semibold text-white"
              onClick={handleShare}
              disabled={loading}
            >
              {loading ? '生成中…' : '生成链接'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
